import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CalendarManager() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', date: '', location: '', category: 'Formation'
  });

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar`);
      setEvents(response.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/calendar/${editingId}`, formData, { withCredentials: true });
        toast.success('Événement mis à jour');
      } else {
        await axios.post(`${API}/calendar`, formData, { withCredentials: true });
        toast.success('Événement créé');
      }
      resetForm();
      fetchEvents();
    } catch (error) { toast.error('Erreur'); }
  };

  const handleEdit = (event) => {
    setEditingId(event.event_id);
    setFormData({
      title: event.title,
      description: event.description,
      date: event.date.split('T')[0],
      location: event.location,
      category: event.category
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    try {
      await axios.delete(`${API}/calendar/${id}`, { withCredentials: true });
      toast.success('Événement supprimé');
      fetchEvents();
    } catch (error) { toast.error('Erreur'); }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', date: '', location: '', category: 'Formation' });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div data-testid="calendar-manager">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Gestion du Calendrier ({events.length})
        </h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary inline-flex items-center" data-testid="add-event-button">
          <Plus size={20} className="mr-2" />{showForm ? 'Annuler' : 'Ajouter'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card mb-6">
          <div className="space-y-4">
            <input type="text" placeholder="Titre" required value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
            <textarea placeholder="Description" required rows={3} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
            <input type="date" required value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
            <input type="text" placeholder="Lieu" required value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
            <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg">
              <option value="Formation">Formation</option>
              <option value="Atelier">Atelier</option>
              <option value="Réunion">Réunion</option>
              <option value="Événement">Événement</option>
              <option value="Visite terrain">Visite terrain</option>
            </select>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary">{editingId ? 'Mettre à jour' : 'Créer'}</button>
              <button type="button" onClick={resetForm} className="btn-secondary">Annuler</button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div></div>
      ) : (
        <div className="space-y-4">
          {events.length === 0 ? (
            <p className="text-center text-[#5C665A] py-8">Aucun événement. Ajoutez le premier événement.</p>
          ) : events.map((event) => (
            <div key={event.event_id} className="card">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs font-medium text-[#D87D4A] px-2 py-1 bg-[#D87D4A]/10 rounded">{event.category}</span>
                    <span className="text-xs text-[#5C665A]">{new Date(event.date).toLocaleDateString('fr-FR')}</span>
                    <span className="text-xs text-[#5C665A]">📍 {event.location}</span>
                  </div>
                  <h3 className="text-lg font-bold text-[#2E332D]">{event.title}</h3>
                  <p className="text-sm text-[#5C665A] mt-1">{event.description}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => handleEdit(event)} className="p-2 hover:bg-gray-100 rounded"><Edit2 size={18} className="text-[#4A6B48]" /></button>
                  <button onClick={() => handleDelete(event.event_id)} className="p-2 hover:bg-gray-100 rounded"><Trash2 size={18} className="text-red-500" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
